/* global YT, Vimeo */

if (!customElements.get('video-component')) {
  // Use the theme's debounce if present, otherwise a small fallback
  const debounce = (fn, wait) => {
    if (typeof window.debounce === 'function') return window.debounce(fn, wait);
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), wait);
    };
  };

  class Video extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.dataset.autoplay === 'true';
      this.background = this.dataset.background === 'true';
      this.naturalWidth = this.dataset.naturalWidth === 'true';
      this.inViewport = false;
      this.pausedByUser = false;
      this.playPromise = null;
      this.observer = null;
    }

    connectedCallback() {
      // Guard against double init if the element is moved in the DOM
      if (this.initialised) return;
      this.initialised = true;
      this.init();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    init() {
      const url = this.dataset.videoUrl;

      if (url) {
        if (url.includes('youtu.be') || url.includes('youtube.com')) {
          this.videoId = Video.getYouTubeId(url);
          this.type = 'youtube';
          this.initYouTube();
        } else if (url.includes('vimeo.com')) {
          this.videoId = url.split('?')[0].split('/').pop();
          this.type = 'vimeo';
          this.initVimeo();
        }
      } else {
        this.type = 'tag';
        this.initVideoTag();
      }

      // Allow for videos which haven't played to play on tap
      if (this.background) {
        const section = this.closest('.video-section');
        if (section) section.addEventListener('click', this.play.bind(this));
      }
    }

    /**
     * Extracts a YouTube video ID from watch, youtu.be, embed, shorts and live URLs.
     * @param {string} url - YouTube URL.
     * @returns {string}
     */
    static getYouTubeId(url) {
      const match = url.match(
        /(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/))([\w-]{11})/
      );
      return match ? match[1] : url.split('?')[0].split('/').pop();
    }

    /**
     * Adds the "played" class to the parent section so the poster/play button hide.
     */
    markPlayed() {
      const section = this.closest('.video-section');
      if (section) section.classList.add('video-section--played');
    }

    /**
     * Loads a player API script.
     * @param {string} src - Url of script to load.
     */
    loadScript(src) {
      if (document.querySelector(`script[src="${src}"]`)) return;

      const s = document.createElement('script');
      s.src = src;
      s.onerror = (err) => console.warn(`Unable to load script ${src}`, err); // eslint-disable-line

      if (this.type === 'vimeo') {
        s.onload = () => document.dispatchEvent(new CustomEvent('on:vimeo-api:loaded'));
      } else if (this.type === 'youtube') {
        window.onYouTubeIframeAPIReady = () => document.dispatchEvent(new CustomEvent('on:youtube-api:loaded'));
      }

      document.body.appendChild(s);
    }

    /**
     * Initialises a YouTube video.
     */
    initYouTube() {
      const initYTPlayer = () => {
        YT.ready(() => {
          this.player = new YT.Player(this.querySelector('div'), {
            videoId: this.videoId,
            width: '1280',
            height: '720',
            playerVars: {
              controls: this.background ? 0 : 1,
              disablekb: this.background ? 1 : 0,
              iv_load_policy: 3,
              modestbranding: 1,
              playsinline: 1,
              rel: 0
            },
            events: {
              onReady: this.handleYTReady.bind(this),
              onStateChange: this.handleYTStateChange.bind(this)
            }
          });
        });
      };

      if (!window.YT) {
        document.addEventListener('on:youtube-api:loaded', initYTPlayer, { once: true });
        this.loadScript('https://www.youtube.com/iframe_api');
      } else {
        initYTPlayer();
      }
    }

    /**
     * Initialises a Vimeo video.
     */
    initVimeo() {
      const initVimeoPlayer = async () => {
        // Passing the full URL (rather than just the ID) also supports private/unlisted links
        this.player = new Vimeo.Player(this, {
          url: this.dataset.videoUrl,
          width: '1280',
          height: '720',
          autoplay: this.autoplay,
          background: this.background,
          keyboard: !this.background,
          muted: this.autoplay
        });

        await this.player.ready();
        this.setupIframe('js-vimeo');

        this.player.on('pause', () => {
          if (this.inViewport) this.pausedByUser = true;
        });

        this.player.on('play', () => {
          this.markPlayed();
          this.dispatchEvent(new CustomEvent('on:media:play', {
            detail: {
              player: this.iframe
            },
            bubbles: true
          }));
        });

        // Only play directly if there's no observer to decide for us
        if (!('IntersectionObserver' in window)) this.play();
      };

      if (!window.Vimeo) {
        document.addEventListener('on:vimeo-api:loaded', initVimeoPlayer, { once: true });
        this.loadScript('https://player.vimeo.com/api/player.js');
      } else {
        initVimeoPlayer();
      }
    }

    /**
     * Initialises a video tag.
     */
    initVideoTag() {
      this.player = this.querySelector('video');
      if (!this.player) return;

      this.player.addEventListener('pause', () => {
        if (this.inViewport) this.pausedByUser = true;
      });

      this.player.addEventListener('play', () => {
        this.markPlayed();
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.player
          },
          bubbles: true
        }));
      });

      // If the video is already playing
      if (this.player.currentTime > 0 && !this.player.paused && !this.player.ended
        && this.player.readyState > 2) {
        this.markPlayed();
        return;
      }

      // Mute the video if a click didn't trigger its load
      const deferredMedia = this.closest('deferred-media');
      if (!deferredMedia || deferredMedia.loadTrigger !== 'click') {
        this.player.muted = true;
      }

      if ('IntersectionObserver' in window) {
        // The observer's first callback will call play() if the video is visible
        this.addObserver(this.player);
      } else {
        this.play();
      }
    }

    /**
     * Handles a YouTube ready event.
     */
    handleYTReady() {
      if (this.autoplay) this.player.mute();
      this.setupIframe('js-youtube');

      if (!('IntersectionObserver' in window)) this.play();
    }

    /**
     * Handles a YouTube state change event.
     * @param {object} e - Event object.
     */
    handleYTStateChange(e) {
      if (e.data === YT.PlayerState.PAUSED) {
        if (this.inViewport) this.pausedByUser = true;
      } else if (e.data === YT.PlayerState.ENDED) {
        // Looping this way rather than 'loop' API parameter to avoid flash of black background
        if (this.background) this.player.playVideo();
      } else if (e.data === YT.PlayerState.PLAYING) {
        this.markPlayed();
        this.dispatchEvent(new CustomEvent('on:media:play', {
          detail: {
            player: this.iframe
          },
          bubbles: true
        }));
      }
    }

    /**
     * Sets up the injected iframe.
     * @param {string} jsClass - Class name to add to the iframe.
     */
    setupIframe(jsClass) {
      // Enable iframe to cover entire container height.
      if (this.autoplay && !this.naturalWidth) {
        this.style.width = `${this.clientHeight * 1.7778}px`;

        window.addEventListener('resize', debounce(() => {
          this.style.width = `${this.clientHeight * 1.7778}px`;
        }, 200));
      }

      this.iframe = this.querySelector('iframe');
      if (!this.iframe) return;

      this.iframe.title = this.dataset.description;
      this.iframe.classList.add(jsClass);
      this.addObserver(this.iframe);
    }

    /**
     * Plays the video (if not paused by the user).
     */
    play() {
      if (!this.player || this.pausedByUser) return;

      if (this.type === 'youtube') {
        if (typeof this.player.playVideo === 'function') this.player.playVideo();
        return;
      }

      // A play request is already in flight
      if (this.playPromise) return;

      const promise = this.player.play();

      if (promise && typeof promise.then === 'function') {
        // Track the pending play() so pause() can wait for it instead of interrupting it
        this.playPromise = promise.then(
          () => {
            this.playPromise = null;
          },
          (err) => {
            this.playPromise = null;
            if (!err) return;

            // Autoplay with sound blocked: retry muted
            if (err.name === 'NotAllowedError' && this.type === 'tag' && !this.player.muted) {
              this.player.muted = true;
              this.play();
            } else if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
              console.warn('Video play failed', err); // eslint-disable-line
            }
          }
        );
      }
    }

    /**
     * Pauses the video (if playing).
     */
    pause() {
      if (!this.player) return;

      // Don't interrupt a pending play() – wait for it to settle, then re-check visibility
      if (this.playPromise) {
        this.playPromise.then(() => {
          if (!this.inViewport) this.pause();
        });
        return;
      }

      if (this.type === 'youtube') {
        if (typeof this.player.getPlayerState === 'function' && this.player.getPlayerState() !== 2) {
          this.player.pauseVideo();
          this.pausedByUser = false;
        }
      } else if (this.type === 'vimeo') {
        this.player.getPaused().then((paused) => {
          if (!paused) {
            this.player.pause();
            this.pausedByUser = false;
          }
        });
      } else if (!this.player.paused) {
        this.player.pause();
        this.pausedByUser = false;
      }
    }

    /**
     * Adds an observer to play the video when in view and pause it when not.
     * @param {Element} el - Element to observe.
     */
    addObserver(el) {
      if ('IntersectionObserver' in window === false) return;
      if (this.observer) this.observer.disconnect();

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.inViewport = true;
            this.play();
          } else {
            this.inViewport = false;
            this.pause();
          }
        });
      }, { rootMargin: '0px 0px 200px 0px' });

      this.observer.observe(el);
    }
  }

  customElements.define('video-component', Video);
}
